// TelcoTrack — required material for milník Připravená bábovka.
// SSOT: user must pick one of the catalog SAP codes in materiál (parity záfuk OK gate).

import type { OkMaterialUsageLike } from './typ-ok-from-material.constants';

/** Allowed SAP codes for bábovka material (Komora malá / Kryt trubičky s markerem). */
export const BABOVKA_REQUIRED_MATERIAL_SAP_CODES = ['327065', '327067'] as const;

export type BabovkaRequiredMaterialSapCode = (typeof BABOVKA_REQUIRED_MATERIAL_SAP_CODES)[number];

export const BABOVKA_REQUIRED_MATERIAL_LABELS: Readonly<
  Record<BabovkaRequiredMaterialSapCode, string>
> = Object.freeze({
  '327065': 'Komora malá - nezpevněný povrch',
  '327067': 'Kryt trubičky s markerem',
});

/**
 * Evidence RD export — „Ukončení před RD kryt OK“.
 * Short cover labels (not full catalog names).
 */
export const BABOVKA_MATERIAL_RD_EXPORT_LABELS: Readonly<
  Record<BabovkaRequiredMaterialSapCode, string>
> = Object.freeze({
  '327065': 'Komora malá',
  '327067': 'Kruhový kryt',
});

/** Hint when bábovka material is missing or not one of the allowed SAP codes. */
export const BABOVKA_MATERIAL_REQUIRED_MESSAGE =
  'Zvolte materiál bábovky v materiálu (327065 nebo 327067).';

const BABOVKA_SAP_SET: ReadonlySet<string> = new Set(BABOVKA_REQUIRED_MATERIAL_SAP_CODES);

function normalizeSap(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

function usageHasQuantity(used: OkMaterialUsageLike): boolean {
  if (used.quantity == null) return true;
  return Number.isFinite(used.quantity) && used.quantity > 0;
}

export function isBabovkaRequiredMaterialSap(sapCode: string | null | undefined): boolean {
  const sap = normalizeSap(sapCode);
  return sap.length > 0 && BABOVKA_SAP_SET.has(sap);
}

/** First allowed bábovka SAP from material usage (327065 before 327067 when both present). */
export function resolveBabovkaRequiredMaterialSap(
  used: readonly OkMaterialUsageLike[] | null | undefined,
): BabovkaRequiredMaterialSapCode | null {
  if (!used?.length) return null;
  const found = new Set<string>();
  for (const row of used) {
    if (!usageHasQuantity(row)) continue;
    const sap = normalizeSap(row.sapCode) || normalizeSap(row.materialCode);
    if (isBabovkaRequiredMaterialSap(sap)) found.add(sap);
  }
  for (const code of BABOVKA_REQUIRED_MATERIAL_SAP_CODES) {
    if (found.has(code)) return code;
  }
  return null;
}

/** True when at least one used row is 327065 or 327067 with positive quantity. */
export function hasRequiredBabovkaMaterial(
  used: readonly OkMaterialUsageLike[] | null | undefined,
): boolean {
  return resolveBabovkaRequiredMaterialSap(used) != null;
}

/** Milestone / phase gate: bábovka material must be present. */
export function isBudovaBabovkaMaterialReady(
  materials: readonly OkMaterialUsageLike[] | null | undefined,
): boolean {
  return hasRequiredBabovkaMaterial(materials);
}

/** RD export label for a bábovka SAP, or empty when unknown/missing. */
export function formatBabovkaMaterialRdExportLabel(
  sapCode: string | null | undefined,
): string {
  const sap = normalizeSap(sapCode);
  if (!isBabovkaRequiredMaterialSap(sap)) return '';
  return BABOVKA_MATERIAL_RD_EXPORT_LABELS[sap as BabovkaRequiredMaterialSapCode];
}
